import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Code,
  Clock,
  Loader2,
  FileText,
  Calendar,
  ArrowRight,
} from "lucide-react";
import AppLayout from "../components/Layout/AppLayout";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { auth } from "../config/firebase";

const API_BASE_URL = import.meta.env.VITE_API_URL;

export default function WidgetHub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [masjidInfo, setMasjidInfo] = useState(null);

  const widgetSections = [
    {
      id: "prayer-times",
      name: "Prayer Times",
      description: "Embed daily prayer times or a monthly prayer calendar on your website",
      icon: Clock,
      gradient: "from-emerald-500/20 to-green-500/10",
      iconBg: "bg-emerald-500/10",
      iconColor: "text-emerald-500",
      route: "/widgets/prayer-times",
      features: [
        "Daily prayer times with day navigation",
        "Monthly calendar with Adhan/Iqama columns",
      ],
    },
    {
      id: "events",
      name: "Events",
      description: "Display your masjid's events in various calendar formats",
      icon: Calendar,
      gradient: "from-purple-500/20 to-pink-500/10",
      iconBg: "bg-purple-500/10",
      iconColor: "text-purple-500",
      route: "/widgets/events",
      features: [
        "Agenda view - scrollable event list",
        "Day view - timeline with event blocks",
        "Month view - traditional calendar grid",
      ],
    },
  ];

  useEffect(() => {
    const loadMasjidInfo = async () => {
      if (!user) return;
      
      try {
        setLoading(true);
        const token = await auth.currentUser?.getIdToken();
        if (!token) return;

        const response = await fetch(`${API_BASE_URL}/v1/masjid/admin/info`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.ok) {
          const data = await response.json();
          setMasjidInfo(data);
        }
      } catch (err) {
        console.error("Error loading masjid info:", err);
      } finally {
        setLoading(false);
      }
    };

    loadMasjidInfo();
  }, [user]);

  const handleWidgetClick = (section) => {
    navigate(section.route);
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex min-h-[70vh] flex-col items-center justify-center gap-6">
          <Loader2 className="h-16 w-16 animate-spin text-primary" />
          <p className="text-lg text-muted-foreground">Loading widgets...</p>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="mx-auto max-w-5xl space-y-8 px-4 py-8">
        {/* Header */}
        <div className="text-center space-y-3">
          <div className="flex items-center justify-center gap-3">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10">
              <Code className="h-7 w-7 text-primary" />
            </div>
          </div>
          <h1 className="text-4xl font-bold tracking-tight">Website Widgets</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Embed prayer times, monthly calendars, and events on your masjid website. Choose a category below to get started.
          </p>
        </div>

        {/* Masjid Info Badge */}
        {masjidInfo?.masjid && (
          <div className="flex justify-center">
            <div className="inline-flex items-center gap-2 rounded-full border bg-card px-4 py-2">
              <div className="h-2 w-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm font-medium">{masjidInfo.masjid.name}</span>
            </div>
          </div>
        )}

        {/* Widget Section Cards */}
        <div className="grid gap-6 md:grid-cols-2">
          {widgetSections.map((section) => {
            const Icon = section.icon;
            return (
              <Card
                key={section.id}
                className={`group relative overflow-hidden border-none bg-gradient-to-br ${section.gradient} shadow-lg transition-all duration-300 cursor-pointer hover:-translate-y-2 hover:shadow-2xl active:scale-[0.98] flex flex-col`}
                onClick={() => handleWidgetClick(section)}
              >
                <CardHeader className="pb-4">
                  <div className={`flex h-16 w-16 items-center justify-center rounded-2xl ${section.iconBg} ${section.iconColor} ring-1 ring-border/50 transition-transform group-hover:scale-110 group-hover:rotate-3`}>
                    <Icon className="h-8 w-8" />
                  </div>
                </CardHeader>
                <CardContent className="flex flex-col flex-1 space-y-4">
                  <div>
                    <CardTitle className="text-2xl mb-2">{section.name}</CardTitle>
                    <CardDescription className="text-sm leading-relaxed">
                      {section.description}
                    </CardDescription>
                  </div>
                  
                  {/* Features List */}
                  <ul className="space-y-2 flex-1">
                    {section.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-2 text-sm text-muted-foreground">
                        <div className={`h-1.5 w-1.5 rounded-full ${section.iconColor}`} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                  
                  <Button
                    variant="outline"
                    className="w-full gap-2 group-hover:bg-primary group-hover:text-primary-foreground mt-auto"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleWidgetClick(section);
                    }}
                  >
                    <ArrowRight className="h-4 w-4" />
                    Choose Widget
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Info Section */}
        <Card className="border-none bg-muted/50">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-blue-500/10">
                <FileText className="h-5 w-5 text-blue-500" />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold">How to use widgets</h3>
                <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground">
                  <li>Select a widget type above</li>
                  <li>Customize the appearance (theme, colors, size)</li>
                  <li>Copy the embed code</li>
                  <li>Paste it into your website's HTML where you want the widget to appear</li>
                </ol>
                <p className="text-xs text-muted-foreground mt-3">
                  💡 The widgets automatically update with your latest prayer times and events. No need to update the code after embedding!
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
